import { useTranslation } from 'react-i18next';
import type { Control, UseFormSetValue } from 'react-hook-form';

import type { ReceiveConfirmLineDto, ReceiveConvoyLocationDto } from '@/entities/receive';
import {
  ConfirmArrivalQuantityCell,
  ConfirmArrivalQuantityFigure,
  getFieldCeiling,
  getRemainingQty,
  redistributeQuantities,
  type ConfirmArrivalQuantityField,
  type ConfirmArrivalUnitMode
} from '@/features/receive-confirm-arrival';
import { SimpleSelect, type SelectOption } from '@/shared/ui/filters';

import { defaultStorageArea } from '../lib/build-payload';
import { useConvoyLine } from '../model/use-convoy-line';
import type { ReceiveConvoyFormValues } from '../model/types';

interface LineCellProps {
  control: Control<ReceiveConvoyFormValues>;
  setValue: UseFormSetValue<ReceiveConvoyFormValues>;
  line: ReceiveConfirmLineDto;
  shipmentIndex: number;
  lineIndex: number;
}

interface QuantityCellProps extends LineCellProps {
  field: ConfirmArrivalQuantityField;
  label: string;
  unitMode: ConfirmArrivalUnitMode;
  secondaryOpen: boolean;
  onToggleSecondary: () => void;
}

/** Good / Damaged / Lost of one line — the single receipt's cell, rebalancing the row as it does. */
export function ReceiveConvoyQuantityCell({
  control,
  setValue,
  line,
  shipmentIndex,
  lineIndex,
  field,
  label,
  unitMode,
  secondaryOpen,
  onToggleSecondary
}: QuantityCellProps) {
  const { name, values, errors } = useConvoyLine(control, shipmentIndex, lineIndex);
  if (!values) return null;

  return (
    <ConfirmArrivalQuantityCell
      id={`${line.lineId}-${field}`}
      label={`${line.lineReference} — ${label}`}
      line={line}
      unitMode={unitMode}
      value={values[field]}
      ceiling={getFieldCeiling(line, field)}
      // One write per edit: the three buckets are rebalanced together.
      onChange={(inventoryQty) =>
        setValue(
          name,
          { ...values, ...redistributeQuantities(line, values, field, inventoryQty) },
          { shouldDirty: true, shouldValidate: true }
        )
      }
      secondaryOpen={secondaryOpen}
      onToggleSecondary={onToggleSecondary}
      error={errors?.[field]?.message}
    />
  );
}

interface LocationCellProps extends Omit<LineCellProps, 'line'> {
  locationOptions: SelectOption[];
  locationsById: Map<string, ReceiveConvoyLocationDto>;
}

/** Receive Location of one line, with its Storage Area underneath when the location has any. */
export function ReceiveConvoyLocationCell({
  control,
  setValue,
  shipmentIndex,
  lineIndex,
  locationOptions,
  locationsById
}: LocationCellProps) {
  const { t } = useTranslation();
  const { name, values, errors } = useConvoyLine(control, shipmentIndex, lineIndex);
  if (!values) return null;

  const storageAreaOptions: SelectOption[] =
    locationsById
      .get(values.targetLocationId)
      ?.storageAreas.map((area) => ({ value: String(area.seqno), label: area.name })) ?? [];

  return (
    <div className="flex flex-col gap-2">
      <SimpleSelect
        options={locationOptions}
        value={values.targetLocationId || undefined}
        // A different location brings a different set of storage areas; the old
        // pick would not exist there.
        onChange={(locationId = '') =>
          setValue(
            name,
            {
              ...values,
              targetLocationId: locationId,
              targetLocationSpaceSeq: defaultStorageArea(locationsById.get(locationId))
            },
            { shouldDirty: true, shouldValidate: true }
          )
        }
        placeholder={t('receive.receivelocation.label', 'Receive Location')}
        className="w-full"
      />
      {errors?.targetLocationId?.message && (
        <p className="text-xs text-destructive">{errors.targetLocationId.message}</p>
      )}
      {/* Mandatory where the location has more than one storage area. */}
      {storageAreaOptions.length > 0 && (
        <SimpleSelect
          options={storageAreaOptions}
          value={values.targetLocationSpaceSeq != null ? String(values.targetLocationSpaceSeq) : undefined}
          onChange={(value) =>
            setValue(`${name}.targetLocationSpaceSeq`, value ? Number(value) : null, {
              shouldDirty: true,
              shouldValidate: true
            })
          }
          placeholder={t('receive.receivelocation.space.label', 'Storage Area')}
          className="w-full"
        />
      )}
      {errors?.targetLocationSpaceSeq?.message && (
        <p className="text-xs text-destructive">{errors.targetLocationSpaceSeq.message}</p>
      )}
    </div>
  );
}

/** What is still outstanding on the line once the entry is subtracted. */
export function ReceiveConvoyRemainingCell({
  control,
  line,
  shipmentIndex,
  lineIndex,
  unitMode
}: Omit<LineCellProps, 'setValue'> & { unitMode: ConfirmArrivalUnitMode }) {
  const { values } = useConvoyLine(control, shipmentIndex, lineIndex);
  if (!values) return null;

  return (
    <ConfirmArrivalQuantityFigure
      line={line}
      unitMode={unitMode}
      quantity={getRemainingQty(line, values)}
      kind="remaining"
    />
  );
}
